import {Platform} from "@variocube/app-api";
import {createQueryString, sleep} from "@variocube/app-common";
import {useStorageItemValue} from "@variocube/cube-ui";
import {useCallback, useState} from "react";
import {useLocalization} from "../../i18n";


export function useSendBookingCode<T>(platform: Platform, code: string, urlKey: string, success: (result: T) => void) {
    const url = useStorageItemValue<string>(urlKey);
    const {language} = useLocalization();
    return useApiRequest(
        platform,
        success,
        url,
        {
            code,
            hostname: platform.hostname,
            locale: language,
        },
        code
    );
}

export function useSendVerificationRequest<T>(platform: Platform, urlKey: string, success: (results: T) => void) {
    let url = useStorageItemValue<string>(urlKey);
    return useApiRequest(
        platform,
        success,
        url
    );
}

function useApiRequest<T>(platform: Platform, success: (result: T) => void, url?: string, body?: Record<string, any>, code?: string) {
    const [loading, setLoading] = useState(false);
    const [invalid, setInvalid] = useState(false);
    const [failed, setFailed] = useState(false);

    const submit = useCallback(async (args: T, params?: Record<string, string>) => {
        if (!url) {
            await sleep(1000);
            return success({...args, code});
        }
        setLoading(true);
        try {
            let requestUrl = url;
            if (params) {
                requestUrl += '?' + createQueryString(params);
            }
            const response = await fetch(requestUrl, {
                method: "post",
                headers: {
                    "content-type": "application/json"
                },
                body: JSON.stringify({
                    ...args,
                    ...body
                })
            });
            if (response.status == 400) {
                setInvalid(true);
            }
            else if (!response.ok) {
                setFailed(true);
            }
            else {
                success(await response.json() as T);
            }
        }
        catch (error) {
            setFailed(true);
        }
        finally {
            setLoading(false);
        }
    }, [platform, url, body]);

    const reset = useCallback(() => {
        setLoading(false);
        setInvalid(false);
        setFailed(false);
    }, [])

    return {loading, invalid, failed, submit, reset};
}